/**
 * PlaygroundBreadcrumbs.tsx
 * Automatic breadcrumb navigation for playground pages
 * 
 * Replaces individual page headers with route-based breadcrumb navigation.
 * Generates breadcrumbs automatically based on current location.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbItem } from '@enfomentis/ui';
import { Home, Layers, Palette, BarChart3, Navigation, MousePointer2, MessageCircle } from 'lucide-react';


interface BreadcrumbRoute {
  path: string;
  label: string;
  icon?: React.ReactNode;
}

/**
 * Generate breadcrumbs based on current route
 */
const useBreadcrumbs = (): BreadcrumbRoute[] => {
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  
  const breadcrumbs: BreadcrumbRoute[] = [];
  
  // Always start with Playground root
  breadcrumbs.push({
    path: '/playground',
    label: 'Playground',
    icon: <Home className="w-4 h-4" />
  });
  
  // Build breadcrumbs from path segments
  if (pathParts.length > 1) {
    const section = pathParts[1]; // playground/[section]
    
    if (section === 'components') {
      breadcrumbs.push({
        path: '/playground/components',
        label: 'Components',
        icon: <Layers className="w-4 h-4" />
      });
      
      // Handle category pages and component pages
      if (pathParts.length > 2 && pathParts[2]) {
        const categoryOrComponent = pathParts[2];
        
        // Check if this is a category page
        if (['navigation', 'layout', 'forms', 'feedback', 'data-display'].includes(categoryOrComponent)) {
          const category = categoryOrComponent;
          let categoryLabel = '';
          let categoryIcon = null;
          
          switch (category) {
            case 'navigation':
              categoryLabel = 'Navigation Components';
              categoryIcon = <Navigation className="w-4 h-4" />;
              break;
            case 'layout':
              categoryLabel = 'Layout Components';
              categoryIcon = <Layers className="w-4 h-4" />;
              break;
            case 'forms':
              categoryLabel = 'Form Controls';
              categoryIcon = <MousePointer2 className="w-4 h-4" />;
              break;
            case 'feedback':
              categoryLabel = 'Feedback & Overlays';
              categoryIcon = <MessageCircle className="w-4 h-4" />;
              break;
            case 'data-display':
              categoryLabel = 'Data Display';
              categoryIcon = <BarChart3 className="w-4 h-4" />;
              break;
          }
          
          breadcrumbs.push({
            path: `/playground/components/${category}`,
            label: categoryLabel,
            icon: categoryIcon
          });
          
          // Individual component page within category
          if (pathParts.length > 3 && pathParts[3]) {
            const component = pathParts[3];
            const componentLabel = component
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
              
            breadcrumbs.push({
              path: `/playground/components/${category}/${component}`,
              label: componentLabel
            });
          }
        } else {
          // Legacy direct component routes (for backward compatibility)
          const component = categoryOrComponent;
          const componentLabel = component
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
            
          breadcrumbs.push({
            path: `/playground/components/${component}`,
            label: componentLabel
          });
        }
      }
    }
    
    else if (section === 'themes') {
      breadcrumbs.push({
        path: '/playground/themes',
        label: 'Themes',
        icon: <Palette className="w-4 h-4" />
      });
    }
    
    else if (section === 'data') {
      breadcrumbs.push({
        path: '/playground/data',
        label: 'Data Visualization',
        icon: <BarChart3 className="w-4 h-4" />
      });
    }
  }
  
  return breadcrumbs;
};

interface PlaygroundBreadcrumbsProps {
  className?: string;
}

/**
 * PlaygroundBreadcrumbs Component
 * 
 * Automatically generates breadcrumbs based on current route.
 * Replaces individual page headers with consistent navigation.
 */
export const PlaygroundBreadcrumbs: React.FC<PlaygroundBreadcrumbsProps> = ({ 
  className 
}) => {
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumbs();
  
  const handleBreadcrumbClick = (path: string) => {
    navigate(path);
  };
  
  if (breadcrumbs.length <= 1) {
    // Don't show breadcrumbs on root playground page
    return null;
  }
  
  return (
    <div className={`px-6 py-4 border-b border-border bg-background ${className || ''}`}>
      <Breadcrumbs 
        variant="light" 
        size="sm"
        className="text-text-secondary"
      >
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          
          return (
            <BreadcrumbItem
              key={crumb.path}
              {...(!isLast && {
                href: '#',
                onPress: () => handleBreadcrumbClick(crumb.path)
              })}
              startContent={crumb.icon}
              className={isLast ? 'text-text-primary font-medium' : 'text-text-secondary hover:text-text-primary'}
            >
              {crumb.label}
            </BreadcrumbItem>
          );
        })}
      </Breadcrumbs>
    </div>
  );
};

export default PlaygroundBreadcrumbs;